import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { gbp } from "../lib/format";
import { CheckCircle2, Coins, Receipt, Download } from "lucide-react";

const monthLabel = (d) => d.toLocaleString("en-GB", { month: "long", year: "numeric" });
const isoDate = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const buildLedger = (investments) => {
  const rows = [];
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  (investments || []).forEach((inv) => {
    const invested = new Date(inv.invested_at);
    // Payout every month on the 5th, starting the month AFTER investment
    let firstPayoutMonth = new Date(invested.getFullYear(), invested.getMonth() + 1, 5);
    const monthly = (inv.amount_gbp * inv.yield_pct) / 100 / 12;
    // Cap at 3 months into the future so investors always see an upcoming schedule
    const horizon = new Date(now.getFullYear(), now.getMonth() + 3, 5);
    while (firstPayoutMonth <= horizon) {
      rows.push({
        id: `${inv.id}-${firstPayoutMonth.getFullYear()}-${firstPayoutMonth.getMonth()}`,
        date: new Date(firstPayoutMonth),
        property_name: inv.property_name,
        property_asset_type: inv.property_asset_type,
        amount: monthly,
        tx_hash: `0x${(inv.id.replace(/-/g, "").slice(0, 6) + firstPayoutMonth.getTime().toString(16).slice(-6)).toUpperCase()}`,
        status: firstPayoutMonth < currentMonthStart ? "Paid" : "Scheduled",
        kind: "Distribution",
      });
      firstPayoutMonth = new Date(firstPayoutMonth.getFullYear(), firstPayoutMonth.getMonth() + 1, 5);
    }
  });

  rows.sort((a, b) => b.date - a.date);
  return rows;
};

const statusChip = (status) => {
  if (status === "Paid") {
    return { color: "#065F46", bg: "#D1FAE5", label: "Paid", Icon: CheckCircle2 };
  }
  return { color: "#8B6F1D", bg: "rgba(212,175,55,0.14)", label: "Scheduled", Icon: Coins };
};

export const DistributionsLedger = ({ investments, userEmail }) => {
  const rows = useMemo(() => buildLedger(investments), [investments]);
  const paidTotal = rows.filter((r) => r.status === "Paid").reduce((s, r) => s + r.amount, 0);
  const upcomingTotal = rows.filter((r) => r.status === "Scheduled").reduce((s, r) => s + r.amount, 0);

  const handleExport = () => {
    const now = new Date();
    const taxYear = now.getMonth() >= 3
      ? `${now.getFullYear()}/${String((now.getFullYear() + 1) % 100).padStart(2, "0")}`
      : `${now.getFullYear() - 1}/${String(now.getFullYear() % 100).padStart(2, "0")}`;
    const lines = [
      "BRICKSTOK · AJBN CONNECT",
      "Distributions & Withdrawals — Tax Pack (MOCK PREVIEW)",
      "".padEnd(72, "="),
      `Investor:          ${userEmail || "—"}`,
      `Generated:         ${now.toLocaleString("en-GB")}`,
      `UK Tax Year:       ${taxYear}`,
      `Report Currency:   GBP`,
      "",
      `Total Paid:        ${gbp(paidTotal, { decimals: 2 })}`,
      `Total Scheduled:   ${gbp(upcomingTotal, { decimals: 2 })}`,
      "",
      "Payout Date  |  Property                          |  Type        |  Status     |  Amount (GBP)  |  Tx Ref",
      "".padEnd(120, "-"),
      ...rows.map((r) =>
        [
          isoDate(r.date).padEnd(12),
          r.property_name.padEnd(34).slice(0, 34),
          (r.property_asset_type || "—").padEnd(11).slice(0, 11),
          r.status.padEnd(10),
          gbp(r.amount, { decimals: 2 }).padStart(13),
          r.tx_hash,
        ].join("  |  ")
      ),
      "",
      "".padEnd(72, "="),
      "This document is a MOCK preview generated for demonstration purposes.",
      "For live tax reporting the auditor of record is BKL. Legal counsel: Harold Benjamin.",
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brickstok-tax-pack-${taxYear.replace("/", "-")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Tax pack downloaded to your device.", {
      description: `File: brickstok-tax-pack-${taxYear.replace("/", "-")}.txt`,
    });
  };

  if (rows.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center"
        data-testid="ledger-empty"
      >
        <div className="overline">Ledger</div>
        <h3 className="mt-2 font-display text-xl font-semibold text-slate-900">No distributions yet</h3>
        <p className="mt-2 text-sm text-slate-500">
          Distribution entries appear here on the 5th of each month following your first allocation.
        </p>
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="distributions-ledger">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-100 px-6 py-5">
        <div>
          <div className="overline flex items-center gap-2">
            <Receipt className="h-3 w-3 brickstok-gold" /> Ledger
          </div>
          <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight text-slate-900">
            Distributions & Withdrawals
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Complete on-chain distribution history for your fractional holdings.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <div className="overline">Paid to date</div>
            <div className="mt-1 font-display text-xl font-semibold text-slate-900 font-mono-num" data-testid="ledger-paid-total">
              {gbp(paidTotal, { decimals: 2 })}
            </div>
          </div>
          <div>
            <div className="overline">Scheduled</div>
            <div className="mt-1 font-display text-xl font-semibold font-mono-num" style={{ color: "#D4AF37" }} data-testid="ledger-scheduled-total">
              {gbp(upcomingTotal, { decimals: 2 })}
            </div>
          </div>
          <Button
            onClick={handleExport}
            className="border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
            variant="outline"
            data-testid="ledger-export-btn"
          >
            <Download className="mr-2 h-4 w-4 brickstok-gold" /> Export Tax Pack
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            <TableHead className="pl-6 uppercase tracking-widest text-[11px] text-slate-500">Payout Date</TableHead>
            <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Description</TableHead>
            <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Kind</TableHead>
            <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Tx Ref</TableHead>
            <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Status</TableHead>
            <TableHead className="pr-6 text-right uppercase tracking-widest text-[11px] text-slate-500">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const chip = statusChip(r.status);
            const Icon = chip.Icon;
            return (
              <TableRow key={r.id} className="hover:bg-slate-50/60" data-testid={`ledger-row-${r.id}`}>
                <TableCell className="pl-6 font-mono-num text-slate-800">
                  <div>{isoDate(r.date)}</div>
                  <div className="text-xs text-slate-500">{monthLabel(r.date)}</div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900">{r.property_name}</div>
                  <div className="text-xs text-slate-500">Monthly rental yield · {r.property_asset_type}</div>
                </TableCell>
                <TableCell>
                  <span className="rounded-sm bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-slate-700">
                    {r.kind}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="rounded-sm bg-slate-900 px-2 py-1 font-mono text-[11px] text-white">{r.tx_hash}</span>
                </TableCell>
                <TableCell>
                  <span
                    className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest"
                    style={{ background: chip.bg, color: chip.color }}
                  >
                    <Icon className="h-3 w-3" /> {chip.label}
                  </span>
                </TableCell>
                <TableCell className="pr-6 text-right font-mono-num text-slate-900">
                  <span style={{ color: r.status === "Paid" ? "#0F172A" : "#8B6F1D" }}>
                    + {gbp(r.amount, { decimals: 2 })}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </section>
  );
};

export default DistributionsLedger;
